"use client";
import * as React from "react";
import { useState } from "react";
import type { Directory, DirectoriesWrapperProps } from "../types";
import CreateDirectory from "../CreateDirectory/index";
import Spacer from "../../shared/Spacer/index";
import EditDirectory from "../EditDirectory/index";
import DirectoryList from "../DirectoryList/index";
import Button from "../../shared/Button/index";
import styles from "./index.module.css";
const DEFAULT_VALUES = {
  directoryListData: [] as Directory[],
  view: "LIST" as "LIST" | "EDIT" | "CREATE",
  directoryToEdit: {} as Directory,
};

function DirectoriesWrapper(props: DirectoriesWrapperProps) {
  const [directories, setDirectories] = useState(
    () => DEFAULT_VALUES.directoryListData
  );

  const [view, setView] = useState(() => DEFAULT_VALUES.view);

  function handleListFetchComplete(directoryList: Directory[]) {
    setDirectories(directoryList);
  }

  function directoriesAdded() {
    return directories.length > 0;
  }

  function dsyncEnabled() {
    return (
      directoriesAdded() &&
      directories.some((directory) => directory.deactivated === false)
    );
  }

  const [directoryToEdit, setDirectoryToEdit] = useState(
    () => DEFAULT_VALUES.directoryToEdit
  );

  function directoryFetchURL() {
    let _url = props.urls.get;
    const [urlPath, qs] = _url.split("?");
    const urlParams = new URLSearchParams(qs);
    if (urlParams.toString()) {
      return `${urlPath}/${directoryToEdit.id}?${urlParams}`;
    }
    return `${urlPath}/${directoryToEdit.id}`;
  }

  function switchToCreateView() {
    setView("CREATE");
  }

  function switchToEditView(directory: Directory) {
    setView("EDIT");
    setDirectoryToEdit(directory);
  }

  function handleActionClick(action: "edit" | "view", directory: any) {
    if (action === "edit") {
      switchToEditView(directory);
    } else {
      typeof props.componentProps?.directoryList?.handleActionClick ===
        "function" &&
        props.componentProps.directoryList.handleActionClick("view", directory);
    }
  }

  function switchToListView() {
    setView("LIST");
  }

  function successHandler(info: {
    connection?: Directory;
    operation: "CREATE" | "UPDATE" | "DELETE" | "COPY";
  }) {
    const { connection, operation } = info;
    if (typeof props.successCallback === "function") {
      props.successCallback({
        operation,
        connection,
      });
    }
    if (operation === "CREATE") {
      switchToEditView(connection!);
    } else if (operation !== "COPY") {
      switchToListView();
    }
  }

  return (
    <div>
      {view === "LIST" ? (
        <div className={styles.listview}>
          <div className={styles.header}>
            <h5 className={styles.h5}>
              {props.title || "Manage DSync Connections"}
            </h5>
            <div className={styles.ctoa}>
              <Button
                name="New Directory"
                handleClick={switchToCreateView}
                classNames={props.classNames?.button?.ctoa}
              />
            </div>
          </div>
          <Spacer y={8} />
          <DirectoryList
            {...props.componentProps?.directoryList}
            urls={{
              get: props.urls.get,
            }}
            handleActionClick={handleActionClick}
            handleListFetchComplete={handleListFetchComplete}
            tenant={props.tenant}
            product={props.product}
          />
        </div>
      ) : null}
      {view === "EDIT" ? (
        <>
          <div className={styles.header}>
            <h5 className={styles.h5}>Edit DSync Connection</h5>
          </div>
          <EditDirectory
            {...props.componentProps?.editDirectory}
            classNames={props.classNames}
            successCallback={successHandler}
            errorCallback={props.errorCallback}
            cancelCallback={switchToListView}
            displayHeader={false}
            urls={{
              patch: `${props.urls.patch}/${directoryToEdit.id}`,
              delete: `${props.urls.delete}/${directoryToEdit.id}`,
              get: directoryFetchURL(),
            }}
          />
        </>
      ) : null}
      {view === "CREATE" ? (
        <>
          <div className={styles.header}>
            <h5 className={styles.h5}>Create DSync Connection</h5>
          </div>
          <Spacer y={5} />
          <CreateDirectory
            {...props.componentProps?.createDirectory}
            classNames={props.classNames}
            successCallback={successHandler}
            errorCallback={props.errorCallback}
            cancelCallback={switchToListView}
            displayHeader={false}
            urls={{
              post: props.urls.post,
            }}
            tenant={props.tenant}
            product={props.product}
          />
        </>
      ) : null}
    </div>
  );
}

export default DirectoriesWrapper;
